import React from 'react';

import { SerializedStyles } from '@emotion/react/dist/emotion-react.cjs';
import { Box, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';

import { DEFAULT_DISCOVER_URL } from '../../const';
import styled, { css } from '../../styled';
import { BREAKPOINTS_VALUES, colorsPalette } from '../../theme';
import { useIsIE } from '../../utils/hooks';
import AnimatedSkeleton from '../AnimatedSkeleton';
import { ConditionalLink } from '../ConditionalLink';
import { LazyLoadImage } from '../LazyloadImage';

export type ExpertCardType = 'light' | 'dark';

const defaultSrcset = [
  { width: 215, height: 215, focus: 'auto' },
  { width: 365, height: 365, focus: 'auto' },
  { width: 220, height: 310, focus: 'auto' },
];
const defaultSizes = `(max-width:${BREAKPOINTS_VALUES['sm']}px) 215px, (max-width: ${BREAKPOINTS_VALUES['md']}) 365px, 500px`;
const defaultTransformation = [
  {
    width: 220,
    height: 310,
    focus: 'auto',
  },
];

interface ExpertCardProps {
  title?: string;
  jobTitle?: string;
  imageUrl?: string;
  slug?: string;
  wrapperCss?: SerializedStyles | ((props: any) => SerializedStyles);
  srcset?: any;
  sizes?: string;
  transformation?: any;
  withLink?: boolean;
  type?: ExpertCardType;
  loading?: boolean;
}

const ExpertCard: React.FC<ExpertCardProps> = ({
  title,
  jobTitle,
  imageUrl,
  slug,
  wrapperCss,
  srcset = defaultSrcset,
  sizes = defaultSizes,
  transformation = defaultTransformation,
  withLink = false,
  type = 'light',
  loading = false,
}) => {
  //check on IE browser to replace srcset with custom image transformation
  const isIE = useIsIE();

  if (loading) {
    return (
      <Wrapper type={type} optionalStyles={wrapperCss}>
        <StyledContainer>
          <LoadingCard />
        </StyledContainer>
      </Wrapper>
    );
  }

  return (
    <Wrapper type={type} optionalStyles={wrapperCss}>
      <StyledContainer>
        <StyledConditionalLink to={`${DEFAULT_DISCOVER_URL}/experts/${slug}`} withLink={withLink}>
          <ImgWrapper type={type}>
            {imageUrl && (
              <StyledLazyLoadImage
                path={imageUrl}
                alt={title}
                srcset={srcset}
                transformation={isIE ? transformation : null}
                sizes={sizes}
                data-object-fit="cover"
                wrapperCss={generatedImgLazyWrapper}
              />
            )}
          </ImgWrapper>
        </StyledConditionalLink>
        <TextWrapper>
          <StyledConditionalLink withLink={withLink} to={`${DEFAULT_DISCOVER_URL}/experts/${slug}`}>
            <Title variant="h3">{title}</Title>
          </StyledConditionalLink>
          <JobTitle>{jobTitle}</JobTitle>
        </TextWrapper>
      </StyledContainer>
    </Wrapper>
  );
};

export default ExpertCard;

const LoadingCard = styled(AnimatedSkeleton)`
  width: 100%;
  height: 100%;
`;

const Wrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'type' || prop !== 'optionalStyles',
})`
  background: ${({ type, theme }) =>
    type === 'dark' ? colorsPalette.blueDark : theme.palette.secondary.light};
  border-radius: 10px;
  height: 300px;
  overflow: hidden;
  position: relative;
  ${({ theme }) => theme.breakpoints.up('md')} {
    height: 348px;
  }
  ${({ optionalStyles }) => optionalStyles}
`;
const StyledContainer = styled(Box)`
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  position: relative;
`;
const StyledConditionalLink = styled(ConditionalLink)`
  color: ${colorsPalette.white};
`;
const ImgWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'type',
})`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 198px;
  overflow: hidden;
  &::after {
    content: '';
    display: block;
    position: absolute;
    left: 0;
    bottom: -3px;
    width: 100%;
    height: 133px;
    background: ${({ type }) =>
      type === 'dark'
        ? 'linear-gradient(0deg, ' +
          colorsPalette.blueDark +
          ' 0%, ' +
          alpha(colorsPalette.blueDark, 0.901961) +
          ' 17.24%, ' +
          alpha(colorsPalette.blueDark, 0) +
          ' 100%)'
        : 'linear-gradient(0deg, ' +
          colorsPalette.blackSecondary +
          ' 0%, ' +
          alpha(colorsPalette.blackSecondary, 0.9) +
          ' 17%, ' +
          alpha(colorsPalette.blackSecondary, 0) +
          ' 100%) 0% 0% no-repeat'};
    -webkit-background-origin: padding-box;
    -o-background-origin: padding-box;
    -moz-background-origin: padding;
    z-index: 0;
  }
  ${({ theme }) => theme.breakpoints.up('md')} {
    height: 274px;
  }
`;
const StyledLazyLoadImage = styled(LazyLoadImage)`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;
const generatedImgLazyWrapper = css`
  width: 100%;
  height: 100%;
  position: relative;
`;
const TextWrapper = styled(Box)`
  padding: 0 24px 21px 24px;
  z-index: 2;
`;
const Title = styled(Typography)`
  & {
    font-size: 18px;
    line-height: 22px;
    font-weight: bold;
    overflow: hidden;
    white-space: inherit;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    margin-bottom: 9px;
    ${({ theme }) => theme.breakpoints.up('md')} {
      font-size: 20px;
      line-height: 25px;
    }
  }
`;
const JobTitle = styled(Typography)`
  font-size: 12px;
  line-height: 15px;
  color: ${colorsPalette.gray};
  ${({ theme }) => theme.breakpoints.up('md')} {
    font-size: 14px;
    line-height: 18px;
  }
`;
